import { getPlatformLabel } from '@/utils/platformUtils';

/**
 * Shared Concept delivery-intent labelling, extracted from `BriefConceptRow.tsx`
 * (L9, `docs/planning/PLAN-campaign-entry-and-chat-integrity-2026-09-23.md`) so
 * `brief-run-concepts.tsx` — the chat-side compact Concept list rendered off
 * `start_brief_order` — can print the identical grammar without importing a
 * component that depends on `useParams`/router context. No behaviour change:
 * this is a straight move, not a rewrite.
 */

/** `Paid · Instagram` / `Organic` — see `BriefConceptRow.tsx`'s original docblock. */
export function deliveryLabel(intent: 'paid' | 'organic', platform: string | null): string {
  const delivery = intent === 'paid' ? 'Paid' : 'Organic';
  return platform?.trim() ? `${delivery} · ${getPlatformLabel(platform)}` : delivery;
}

/**
 * True when a Concept's chosen paid platform is one `public.ad_copy_platforms`
 * (via `useAdCopyPlatforms`) has flagged `supportsCopyRevision: false` — i.e.
 * copy for this platform can exist and be approved, but can never be made the
 * Concept's current copy or travel to a paid delivery/readout
 * (`select_brief_concept_copy_revision` refuses it outright; see
 * `AdCopyPlatformOption`'s doc comment in `AdUnitsService.ts`). Matched
 * case-insensitively against the registry's id AND label, because this
 * function's platform comes from the free-text legacy field
 * (`project_task_delivery_intents.platform`), not the registry-backed
 * dropdown — so "LinkedIn", "linkedin" and the registry's own label must all
 * resolve the same. Silent (no match) for anything the registry doesn't
 * name — Instagram in particular resolves through Meta's own ad account and
 * is never flagged here, matching the "silence rather than a guess" rule
 * this file already follows for ratios.
 */
export function isUnsupportedPaidPlatform(
  platform: string | null,
  unsupportedPlatforms: readonly { id: string; label: string }[] | undefined,
): boolean {
  const normalized = platform?.trim().toLowerCase();
  if (!normalized || !unsupportedPlatforms?.length) return false;
  return unsupportedPlatforms.some(
    (entry) => entry.id.toLowerCase() === normalized || entry.label.toLowerCase() === normalized,
  );
}

/**
 * A Concept's stored `summary` is the composer's message direction: "Beat 1: ...
 * Beat 2: ..." notes for whoever writes the copy, not a caption. A preview that
 * has no written copy yet fell back to it and printed "Beat 1: ..." as the post
 * caption (ITC run ca9919d4, the Tax Directors Concept). Returns the summary only
 * when it is not beat notes; otherwise an empty string, so the preview shows
 * nothing rather than the internal plan.
 */
export function captionFromSummary(summary: string | null | undefined): string {
  const text = summary?.trim() ?? '';
  if (!text) return '';
  return /^beat\s*\d+\s*[:.\-–—]/i.test(text) ? '' : text;
}
